import { SpinnerSize } from '@fluentui/react';
import { IComponent, Loader, LoaderType, useTheme } from '@h2oai/ui-kit';
import { forwardRef, HTMLAttributes, Ref, useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { FormattedUsageDataPoint } from '../types';

/** Styles interface for TimeSeriesStacked component */
export interface ITimeSeriesStackedStyles {
  root?: React.CSSProperties;
}

/** Tooltip payload entry from Recharts */
interface TooltipPayloadEntry {
  dataKey: string;
  value: number;
  fill: string;
  payload: FormattedUsageDataPoint;
}

/** Custom tooltip props */
interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
}

/** Legend payload entry from Recharts */
interface LegendPayloadEntry {
  value: string;
  color: string;
}

/** Custom legend props */
interface CustomLegendProps {
  payload?: LegendPayloadEntry[];
}

export type UnitType = 'ai_units' | 'tokens';

export interface ITimeSeriesStackedProps extends IComponent<ITimeSeriesStackedStyles>, HTMLAttributes<HTMLDivElement> {
  data: FormattedUsageDataPoint[];
  groups: string[];
  loadingMessage?: string;
  getTooltipMessage?: (datum: FormattedUsageDataPoint) => string;
  showLegend?: boolean;
  groupColors?: Record<string, string>;
  unitType?: UnitType;
}

export const TimeSeriesStacked = forwardRef((props: ITimeSeriesStackedProps, ref: Ref<HTMLDivElement>) => {
  const {
    data,
    groups,
    loadingMessage,
    getTooltipMessage,
    showLegend = true,
    groupColors: externalGroupColors,
    unitType = 'ai_units',
    className,
    ...elementProps
  } = props;

  const theme = useTheme();

  // Observable10 color palette from D3 - Default palette (10 colors)
  // https://d3js.org/d3-scale-chromatic/categorical#schemeObservable10
  const OBSERVABLE10_COLORS = [
    '#4269d0', // Blue
    '#FFDD00', // Summer Yellow
    '#ff725c', // Coral
    '#6cc5b0', // Teal
    '#3ca951', // Green
    '#ff8ab7', // Pink
    '#a463f2', // Purple
    '#97bbf5', // Light Blue
    '#9c6b4e', // Brown
    '#9498a0', // Gray
  ];

  // Set3 color palette from D3 (12 colors)
  // https://d3js.org/d3-scale-chromatic/categorical#schemeSet3
  const SET3_COLORS = [
    '#8dd3c7', // Teal
    '#ffffb3', // Light Yellow
    '#bebada', // Light Purple
    '#fb8072', // Salmon
    '#80b1d3', // Sky Blue
    '#fdb462', // Orange
    '#b3de69', // Light Green
    '#fccde5', // Pink
    '#d9d9d9', // Gray
    '#bc80bd', // Purple
    '#ccebc5', // Mint
    '#ffed6f', // Yellow
  ];

  // Tableau10 color palette from D3 (10 colors)
  // https://d3js.org/d3-scale-chromatic/categorical#schemeTableau10
  const TABLEAU10_COLORS = [
    '#4e79a7', // Blue
    '#FFDD00', // Summer Yellow
    '#e15759', // Red
    '#76b7b2', // Teal
    '#59a14f', // Green
    '#f28e2c', // Orange
    '#af7aa1', // Purple
    '#ff9da7', // Pink
    '#9c755f', // Brown
    '#bab0ab', // Gray
  ];

  // Company color palette - ordered for optimal visual contrast (11 colors)
  const COMPANY_COLORS = [
    '#FFDD00', // Summer Yellow
    '#666666', // Medium Gray - 2
    '#FFE600', // Sunshine Yellow
    '#373630', // Dark Gray
    '#FFEB66', // Medium Yellow
    '#999999', // Medium Gray - 1
    '#FFF199', // Light Yellow - 1
    '#000000', // Black
    '#FFF8CC', // Light Yellow - 2
    '#EBEBEB', // Light Gray - 2
    '#F9F9F9', // Light Gray
  ];

  // Determine which palette to use based on environment variable
  // Options: 'tableau10' (default), 'observable10', 'set3', 'company'
  const colorScheme = process.env.REACT_APP_COLOR_SCHEME || 'tableau10';
  const BASE_COLORS =
    colorScheme === 'company'
      ? COMPANY_COLORS
      : colorScheme === 'tableau10'
      ? TABLEAU10_COLORS
      : colorScheme === 'set3'
      ? SET3_COLORS
      : OBSERVABLE10_COLORS;

  // Generate additional colors if needed
  const generateAdditionalColors = (count: number): string[] => {
    const colors: string[] = [];

    if (colorScheme === 'company') {
      // Mix of yellows and grays for company colors
      const yellowHue = 50;
      const grayHue = 0;

      for (let i = 0; i < count; i++) {
        if (i % 2 === 0) {
          const lightness = 85 - i * 3;
          const saturation = 100 - i * 5;
          colors.push(`hsl(${yellowHue}, ${Math.max(saturation, 40)}%, ${Math.max(lightness, 30)}%)`);
        } else {
          const lightness = 75 - i * 4;
          colors.push(`hsl(${grayHue}, 0%, ${Math.max(lightness, 20)}%)`);
        }
      }
    } else {
      // Generate shades based on the current base palette (Set3 or Tableau10)
      for (let i = 0; i < count; i++) {
        const baseColorIndex = i % BASE_COLORS.length;
        const baseColor = BASE_COLORS[baseColorIndex];
        // Darken/lighten the base color slightly for variations
        const factor = Math.floor(i / BASE_COLORS.length) + 1;
        const adjustment = factor * 15;
        colors.push(`color-mix(in srgb, ${baseColor} ${100 - adjustment}%, ${factor % 2 === 0 ? 'white' : 'black'})`);
      }
    }

    return colors;
  };

  // Select colors based on number of groups
  const selectColors = (groupCount: number): string[] => {
    if (groupCount <= BASE_COLORS.length) {
      // Use the base palette colors
      return BASE_COLORS.slice(0, groupCount);
    } else {
      // Use all base colors + dynamically generated ones
      const additionalCount = groupCount - BASE_COLORS.length;
      return [...BASE_COLORS, ...generateAdditionalColors(additionalCount)];
    }
  };

  const groupColors = useMemo(() => {
    // Use external colors if provided, otherwise compute internally
    if (externalGroupColors) {
      return groups.map((group) => externalGroupColors[group] || theme.palette?.gray600 || '#cccccc');
    }
    return selectColors(groups.length);
  }, [groups, externalGroupColors]);

  // Unit-specific formatting
  const isTokens = unitType === 'tokens';
  const unitLabel = isTokens ? 'Tokens' : 'AI Units';
  const yAxisLabel = isTokens ? 'Tokens (Millions)' : 'AI Units';

  // Format large numbers for Y-axis (millions for tokens, standard for AI units)
  const formatYAxisTick = (value: number): string => {
    if (isTokens) {
      if (value >= 1_000_000) {
        const millions = value / 1_000_000;
        return millions % 1 === 0 ? `${millions}M` : `${millions.toFixed(1)}M`;
      }
      if (value >= 1_000) {
        return `${(value / 1_000).toFixed(0)}K`;
      }
    }
    return value.toLocaleString();
  };

  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
      const datum = payload[0].payload;
      const totalValue = groups.reduce((sum, group) => {
        const value = datum[group];
        return sum + (typeof value === 'number' ? value : 0);
      }, 0);

      return (
        <div
          style={{
            backgroundColor: theme.palette?.white,
            padding: '8px 12px',
            border: `1px solid ${theme.palette?.gray300}`,
            borderRadius: '4px',
            boxShadow: `0 2px 4px ${theme.palette?.gray900}1a`,
          }}
        >
          <p style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>{datum.time}</p>
          <p style={{ margin: '4px 0', fontSize: '13px', fontWeight: 500 }}>
            Total: {totalValue.toLocaleString()} {unitLabel}
          </p>
          <div style={{ marginTop: 8, borderTop: `1px solid ${theme.palette?.gray300}`, paddingTop: 8 }}>
            {payload.map((entry: TooltipPayloadEntry, index: number) => (
              <div key={`item-${index}`} style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
                <div
                  style={{
                    width: 12,
                    height: 12,
                    backgroundColor: entry.fill,
                    marginRight: 8,
                    borderRadius: 2,
                  }}
                />
                <span style={{ fontSize: '12px', color: theme.palette?.gray600, flex: 1 }}>
                  {entry.dataKey}: {entry.value.toLocaleString()} {unitLabel}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom legend renderer to improve accessibility
  const CustomLegend = ({ payload }: CustomLegendProps) => {
    return (
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '16px',
          padding: '8px 0',
        }}
      >
        {payload?.map((entry: LegendPayloadEntry, index: number) => (
          <div
            key={`legend-item-${index}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <div
              style={{
                width: 14,
                height: 14,
                backgroundColor: entry.color,
                borderRadius: 2,
              }}
            />
            <span style={{ fontSize: 12, color: theme.palette?.black }}>{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      {...elementProps}
      ref={ref}
      className={className}
      data-test={props['data-test'] || 'timeSeriesStacked'}
      style={{ width: '100%', height: '100%', position: 'relative' }}
    >
      {loadingMessage && (
        <Loader
          loaderContainerStyles={{ root: { position: 'absolute', zIndex: 1, height: '100%', width: '100%' } }}
          styles={{
            root: { height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' },
          }}
          type={LoaderType.spinner}
          size={SpinnerSize.large}
          label={loadingMessage}
        />
      )}
      <div
        style={
          loadingMessage
            ? { opacity: 0.5, pointerEvents: 'none', width: '100%', height: '100%' }
            : { width: '100%', height: '100%' }
        }
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette?.gray200} />
            <XAxis
              dataKey="time"
              tick={{ fontSize: 12 }}
              tickLine={{ stroke: theme.palette?.gray300 }}
              axisLine={{ stroke: theme.palette?.gray300 }}
            />
            <YAxis
              domain={[0, 'auto']}
              tick={{ fontSize: 12 }}
              tickLine={{ stroke: theme.palette?.gray300 }}
              axisLine={{ stroke: theme.palette?.gray300 }}
              tickFormatter={formatYAxisTick}
              label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: theme.palette?.gray200 || 'rgba(0, 0, 0, 0.05)' }} />
            {showLegend && <Legend content={<CustomLegend />} />}
            {groups.map((group, index) => (
              <Bar
                key={group}
                dataKey={group}
                stackId="usage"
                fill={groupColors[index]}
                radius={index === groups.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
});
